import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { darkenHexColor, lightenHexColor } from "../../utilities/parsers";

// All measurements below are in the SVG's own design-space units (the
// original artwork was authored on a 553x120 canvas with a 48px label).
// Everything scales off of that reference.
const BASE_FONT_SIZE = 48;
const DESIGN_WIDTH = 553;
const DESIGN_HEIGHT = 120;
const TEXT_CENTER_X = 276.5;
const CONTENT_TOP = 24;

// How much horizontal room the body offers a label before it needs to
// stretch, and how much breathing room to keep around the measured text.
// TEXT_SAFE_WIDTH is set close to the body's actual native interior capacity
// (459px between the fangs) - text under that amount now shrinks the body
// (down to MIN_DELTA) instead of always sitting at full native width, so a
// short label doesn't leave a lot of empty black space beside it.
const TEXT_SAFE_WIDTH = 420;
const SIDE_PADDING = 24;
const MIN_DELTA = -200;

// The badge only ever grows/shrinks in exact proportion to the configured
// font size (never distorts), clamped so a stray value can't blow up the layout.
// The floor is much lower than the other badges' because BASE_FONT_SIZE here
// (48) is itself much larger, so realistic configured sizes (well under 24)
// must still be able to scale down instead of hitting the floor early.
const MIN_FONT_SCALE = 0.15;
const MAX_FONT_SCALE = 2.5;

// This artwork's native text (48px) is much larger than the others', so it
// needs a much larger DISPLAY_SCALE to land at a comparable on-screen size
// for a typical configured font size.
const DISPLAY_SCALE = 0.6;

// Extra px nudge to keep the badge visually anchored to the card's top edge
// as the font size (and therefore renderedHeight) grows - tuned by eye per
// size band, since the proportional offset alone drifts at larger sizes.
const TOP_OFFSET_BREAKPOINTS: Array<[maxFontSize: number, offset: number]> = [
    [13, 4],
    [16, 6],
    [18, 7],
    [20, 8],
    [22, 9],
    [25, 10],
    [27, 11],
    [29, 12],
    [32, 13],
    [33, 14],
    [33, 14],
    [36, 15],
    [39, 16],
    [42, 17],
    [44, 18],
    [46, 19],
    [48, 20],
];
const TOP_OFFSET_OVERFLOW = 21;

// The whole badge is built from exactly two seed colors - every other tone
// (gradient stops, strokes) is derived from these via lighten/darken so the
// artwork stays a coherent 2-color palette.
const DEFAULT_PRIMARY_COLOR = "#111111";
const DEFAULT_SECONDARY_COLOR = "#f36a0a";

const estimateTextWidth = (text: string) => text.length * BASE_FONT_SIZE * 0.55;

type DiscountFlagBadgeProps = {
    text: string;
    fontSize?: number;
    fontWeight?: string | number;
    fontStyle?: string;
    color?: string;
    primaryColor?: string;
    secondaryColor?: string;
    style?: CSSProperties;
    [dataAttr: `data-${string}`]: unknown;
};

const DiscountFlagBadge = ({
    text,
    fontSize,
    fontWeight,
    fontStyle,
    color,
    primaryColor,
    secondaryColor,
    style,
    ...rest
}: DiscountFlagBadgeProps) => {
    const uid = useId();
    const textRef = useRef<SVGTextElement>(null);
    const [textWidth, setTextWidth] = useState(() => estimateTextWidth(text || ""));

    const measure = () => {
        const node = textRef.current;
        if (!node) return;
        try {
            const box = node.getBBox();
            if (box.width > 0) setTextWidth(box.width);
        } catch {
            // node not measurable yet (e.g. not laid out) - keep last known width
        }
    };

    useLayoutEffect(() => {
        measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    useEffect(() => {
        const fonts = (document as any)?.fonts;
        if (fonts?.ready) {
            fonts.ready.then(measure).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    if (!text) return null;

    const safeFontSize = fontSize && fontSize > 0 ? fontSize : BASE_FONT_SIZE;
    const fontScale = Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, safeFontSize / BASE_FONT_SIZE));

    const delta = Math.max(MIN_DELTA, textWidth + SIDE_PADDING * 2 - TEXT_SAFE_WIDTH);
    const designWidth = DESIGN_WIDTH + delta;

    const finalScale = DISPLAY_SCALE * fontScale;
    const renderedWidth = designWidth * finalScale;
    const renderedHeight = DESIGN_HEIGHT * finalScale;
    const textX = TEXT_CENTER_X + delta / 2;

    const shadowId = `fang-shadow-${uid}`;
    const blackId = `fang-black-${uid}`;
    const fangBlackId = `fang-blackLight-${uid}`;
    const orangeId = `fang-orange-${uid}`;

    const topOffset =
        TOP_OFFSET_BREAKPOINTS.find(([maxFontSize]) => safeFontSize <= maxFontSize)?.[1] ?? TOP_OFFSET_OVERFLOW;

    const primary = primaryColor || DEFAULT_PRIMARY_COLOR;
    const secondary = secondaryColor || DEFAULT_SECONDARY_COLOR;

    // Neutral family: the fangs and the main body's black gradient.
    const primaryLight = lightenHexColor(primary, 8);
    const primaryDark = darkenHexColor(primary, 4);

    // Accent family: the fang/body border gradient.
    const secondaryLight = lightenHexColor(secondary, 20);
    const secondaryDark = darkenHexColor(secondary, 15);

    return (
        <svg
            {...rest}
            width={renderedWidth}
            height={renderedHeight}
            viewBox={`0 0 ${designWidth} ${DESIGN_HEIGHT}`}
            style={{
                position: "absolute",
                top: -((CONTENT_TOP / DESIGN_HEIGHT) * renderedHeight + topOffset),
                right: 6,
                overflow: "visible",
                zIndex: 10,
                ...style,
            }}
            role="img"
            aria-label={text}
        >
            <defs>
                <linearGradient id={blackId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={primaryLight} />
                    <stop offset="45%" stopColor={primary} />
                    <stop offset="100%" stopColor={primaryDark} />
                </linearGradient>
                <linearGradient id={fangBlackId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={primaryLight} />
                    <stop offset="100%" stopColor={primary} />
                </linearGradient>
                <linearGradient id={orangeId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={secondaryLight} />
                    <stop offset="45%" stopColor={secondary} />
                    <stop offset="100%" stopColor={secondaryDark} />
                </linearGradient>
                <filter id={shadowId} x="-15%" y="-30%" width="130%" height="170%">
                    <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000000" floodOpacity="0.3" />
                </filter>
            </defs>

            <g filter={`url(#${shadowId})`}>
                {/* Left fang: fixed, anchored to the left edge of the body */}
                <path
                    d="M47 24L10 57H47Z"
                    fill={`url(#${fangBlackId})`}
                    stroke={`url(#${orangeId})`}
                    strokeWidth={3}
                    strokeLinejoin="round"
                />

                {/* Right fang: rigidly translated as the body stretches */}
                <g transform={`translate(${delta} 0)`}>
                    <path
                        d="M506 24L543 57H506Z"
                        fill={`url(#${fangBlackId})`}
                        stroke={`url(#${orangeId})`}
                        strokeWidth={3}
                        strokeLinejoin="round"
                    />
                </g>

                {/* Body: the only part that stretches to fit the label - left edge and
                    rounded corners stay put, the right edge and its rounded corner move */}
                <path
                    d={`M47 24H${506 + delta}V88C${506 + delta} 101.3 ${495.3 + delta} 112 ${482 + delta} 112H71C57.7 112 47 101.3 47 88Z`}
                    fill={`url(#${blackId})`}
                    stroke={`url(#${orangeId})`}
                    strokeWidth={3}
                    strokeLinejoin="round"
                />

                <path
                    d={`M50 29H${503 + delta}`}
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth={0.8}
                    strokeOpacity={0.08}
                    strokeLinecap="round"
                />
            </g>

            <text
                ref={textRef}
                x={textX}
                y={72}
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily="Arial, Helvetica, sans-serif"
                fontSize={BASE_FONT_SIZE}
                fontWeight={fontWeight || 700}
                letterSpacing={-1.1}
                fontStyle={fontStyle || "normal"}
                fill={color || "#ffffff"}
            >
                {text}
            </text>
        </svg>
    );
};

export default DiscountFlagBadge;
